import React from 'react';
import { Link } from 'react-router-dom';

/**
 * TrialExpired - Shows when a trial user has used their free storm event
 * @param {string} variant - 'modal' (default), 'page', or 'banner'
 * @param {function} onClose - Optional callback to close modal variant
 */
function TrialExpired({ variant = 'modal', onClose }) {
  if (variant === 'banner') {
    return (
      <div className="trial-expired-banner">
        <span className="trial-banner-icon">⚡</span>
        <span className="trial-banner-text">
          <strong>Trial Complete!</strong> You've used your free storm event.
        </span>
        <Link to="/pricing" className="trial-banner-btn">
          Upgrade Now
        </Link>
        <style>{styles}</style>
      </div>
    );
  }

  if (variant === 'page') {
    return (
      <div className="trial-expired-page">
        <div className="trial-expired-content">
          <div className="trial-icon">🎉</div>
          <h1 className="trial-title">Trial Complete!</h1>
          <p className="trial-subtitle">
            You've successfully tested LawnTrack™ with your free storm event.
          </p>

          <div className="trial-benefits">
            <h3>Ready to upgrade? Here's what you'll get:</h3>
            <ul>
              <li>✓ Unlimited storm events</li>
              <li>✓ Route optimization</li>
              <li>✓ Customer invoicing</li>
              <li>✓ Service history tracking</li>
              <li>✓ Payment tracking</li>
            </ul>
          </div>

          <div className="trial-cta">
            <Link to="/pricing" className="trial-upgrade-btn">
              View Plans & Upgrade
            </Link>
            <p className="trial-note">Plans start at just $10/month</p>
          </div>

          <div className="trial-access-note">
            <p>
              <strong>You can still access:</strong> Your customer list, service history, and all your data.
              You just can't create new routes or complete stops until you upgrade.
            </p>
          </div>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  // Default: modal variant
  return (
    <div className="trial-expired-overlay">
      <div className="trial-expired-modal">
        <button className="trial-close-btn" onClick={onClose}>×</button>
        <div className="trial-icon">⚡</div>
        <h2 className="trial-title">Trial Complete!</h2>
        <p className="trial-subtitle">
          You've used your free storm event. Upgrade to continue using LawnTrack™.
        </p>

        <div className="trial-features">
          <div className="trial-feature">
            <span className="feature-icon">📊</span>
            <span>Unlimited storms</span>
          </div>
          <div className="trial-feature">
            <span className="feature-icon">🚀</span>
            <span>Route optimization</span>
          </div>
          <div className="trial-feature">
            <span className="feature-icon">💰</span>
            <span>Payment tracking</span>
          </div>
        </div>

        <Link to="/pricing" className="trial-upgrade-btn">
          View Plans & Upgrade
        </Link>

        <p className="trial-dismiss" onClick={onClose}>
          I'll stay on free for now
        </p>
        <style>{styles}</style>
      </div>
    </div>
  );
}

const styles = `
  /* Banner variant */
  .trial-expired-banner {
    display: flex;
    align-items: center;
    gap: 12px;
    background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
    border: 1px solid #ef4444;
    border-radius: 8px;
    padding: 12px 16px;
    margin: 12px 0;
  }

  .trial-banner-icon {
    font-size: 1.25rem;
  }

  .trial-banner-text {
    flex: 1;
    font-size: 0.9rem;
    color: #991b1b;
  }

  .trial-banner-btn {
    background: #ef4444;
    color: white;
    text-decoration: none;
    padding: 8px 20px;
    border-radius: 6px;
    font-size: 0.85rem;
    font-weight: 600;
    transition: background 0.2s ease;
  }

  .trial-banner-btn:hover {
    background: #dc2626;
    color: white;
  }

  /* Modal variant */
  .trial-expired-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
  }

  .trial-expired-modal {
    background: white;
    border-radius: 16px;
    padding: 32px;
    max-width: 420px;
    width: 100%;
    text-align: center;
    position: relative;
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
  }

  .trial-close-btn {
    position: absolute;
    top: 12px;
    right: 12px;
    background: none;
    border: none;
    font-size: 1.5rem;
    color: #9ca3af;
    cursor: pointer;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: all 0.2s;
  }

  .trial-close-btn:hover {
    background: #f3f4f6;
    color: #4b5563;
  }

  .trial-icon {
    font-size: 3rem;
    margin-bottom: 16px;
  }

  .trial-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #111827;
    margin: 0 0 8px 0;
  }

  .trial-subtitle {
    font-size: 1rem;
    color: #6b7280;
    margin: 0 0 24px 0;
    line-height: 1.5;
  }

  .trial-features {
    display: flex;
    justify-content: center;
    gap: 16px;
    margin-bottom: 24px;
    flex-wrap: wrap;
  }

  .trial-feature {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.9rem;
    color: #374151;
  }

  .feature-icon {
    font-size: 1.1rem;
  }

  .trial-upgrade-btn {
    display: inline-block;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    color: white;
    text-decoration: none;
    padding: 14px 32px;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 600;
    transition: all 0.2s ease;
    border: none;
    cursor: pointer;
  }

  .trial-upgrade-btn:hover {
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
    color: white;
  }

  .trial-dismiss {
    margin-top: 16px;
    color: #9ca3af;
    font-size: 0.85rem;
    cursor: pointer;
    transition: color 0.2s;
  }

  .trial-dismiss:hover {
    color: #6b7280;
  }

  /* Page variant */
  .trial-expired-page {
    min-height: 100vh;
    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
  }

  .trial-expired-content {
    background: white;
    border-radius: 20px;
    padding: 48px 40px;
    max-width: 520px;
    width: 100%;
    text-align: center;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  }

  .trial-benefits {
    background: #f8fafc;
    border-radius: 12px;
    padding: 24px;
    margin: 24px 0;
    text-align: left;
  }

  .trial-benefits h3 {
    font-size: 1rem;
    font-weight: 600;
    color: #374151;
    margin: 0 0 16px 0;
  }

  .trial-benefits ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .trial-benefits li {
    padding: 8px 0;
    color: #059669;
    font-size: 0.95rem;
  }

  .trial-cta {
    margin: 32px 0 24px;
  }

  .trial-note {
    margin-top: 12px;
    color: #9ca3af;
    font-size: 0.85rem;
  }

  .trial-access-note {
    background: #f0fdf4;
    border: 1px solid #86efac;
    border-radius: 8px;
    padding: 16px;
  }

  .trial-access-note p {
    margin: 0;
    font-size: 0.9rem;
    color: #166534;
    line-height: 1.5;
  }

  /* Responsive */
  @media (max-width: 480px) {
    .trial-expired-modal {
      padding: 24px 20px;
    }

    .trial-features {
      flex-direction: column;
      gap: 8px;
    }

    .trial-expired-content {
      padding: 32px 24px;
    }
  }
`;

export default TrialExpired;
